import React, { Component } from 'react';
import { isEqual } from 'lodash';
import {
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  Typography,
  FormHelperText,
  Stack,
  TextField,
} from '@mui/material';

/*
Renders the `attribs` object of a script element from the `attribsField` schema
declared on the element type in ScriptEdit.

`fields` is a list of field descriptors:
  field_name  key written into attribs (required)
  label       display label (required)
  datatype    'string' | 'textarea' | 'number' | 'boolean'
  default     seeds a boolean control while the key is absent
  placeholder / helperText / suggestions (optional)

  string   -> single-line text input
  textarea -> multiline text area
  number   -> numeric input (stored as a number)
  boolean  -> checkbox

The schema only describes the keys we know about. Keys outside it are carried
through untouched and stay editable in the raw JSON section, so a schema can
never block an element type from using an attrib the mobile app understands.
*/

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

export const asAttribsObject = (v) => (isPlainObject(v) ? v : {});

const stringifyAttribs = (v) => JSON.stringify(asAttribsObject(v), null, 2);

export default class AttribsFields extends Component {
  static defaultProps = {
    fields: [],
    value: {},
    onChange: () => null,
  };

  constructor(props) {
    super(props);

    this.state = {
      // Raw text per text/number field so partial entries ('-', '1.') survive.
      drafts: {},
      jsonDraft: stringifyAttribs(props.value),
      jsonError: null,
      rawOpen: false,
    };

    // The last object we pushed up from the JSON editor. When it comes back
    // down through props we leave the textarea alone rather than reformatting
    // what the user is still typing.
    this.lastJsonEmit = null;
    this.uid = '_' + Math.floor(Math.random() * 1000000000);
  }

  componentDidUpdate(prevProps) {
    if (isEqual(prevProps.value, this.props.value)) return;
    if (isEqual(this.props.value, this.lastJsonEmit)) return;
    this.setState({
      jsonDraft: stringifyAttribs(this.props.value),
      jsonError: null,
    });
  }

  get attribs() {
    return asAttribsObject(this.props.value);
  }

  get fieldList() {
    return Array.isArray(this.props.fields) ? this.props.fields : [];
  }

  emit = (next) => {
    this.lastJsonEmit = null;
    this.props.onChange(next);
  };

  setKey = (field_name, val) => {
    this.emit({ ...this.attribs, [field_name]: val });
  };

  clearKey = (field_name) => {
    const next = { ...this.attribs };
    delete next[field_name];
    this.emit(next);
  };

  onTextChange = (field, text) => {
    this.setState((s) => ({ drafts: { ...s.drafts, [field.field_name]: text } }));

    if (text === '') {
      this.clearKey(field.field_name);
      return;
    }

    if (field.datatype === 'number') {
      const num = Number(text);
      // Leave the last good value in place while the draft is unparseable.
      if (Number.isFinite(num)) this.setKey(field.field_name, num);
      return;
    }

    this.setKey(field.field_name, text);
  };

  onJsonChange = (e) => {
    const text = e.target.value;

    let parsed;
    try {
      parsed = JSON.parse(text || '{}');
    } catch (err) {
      this.setState({ jsonDraft: text, jsonError: err.message });
      return;
    }

    if (!isPlainObject(parsed)) {
      this.setState({
        jsonDraft: text,
        jsonError: 'Attributes must be a JSON object.',
      });
      return;
    }

    this.lastJsonEmit = parsed;
    this.setState({ jsonDraft: text, jsonError: null, drafts: {} });
    this.props.onChange(parsed);
  };

  getTextValue = (field) => {
    const draft = this.state.drafts[field.field_name];
    if (draft !== undefined) return draft;

    const raw = this.attribs[field.field_name];
    if (raw === undefined || raw === null) return '';
    return isPlainObject(raw) || Array.isArray(raw) ? JSON.stringify(raw) : String(raw);
  };

  renderBoolean(field) {
    const raw = this.attribs[field.field_name];
    const checked = typeof raw === 'boolean' ? raw : field.default === true;

    return (
      <Stack key={field.field_name} spacing={0.25}>
        <FormControlLabel
          sx={{ m: 0, fontWeight: 400 }}
          control={
            <Checkbox
              size="small"
              checked={checked}
              onChange={(e, val) => this.setKey(field.field_name, val)}
              color="primary"
              sx={{ py: 0.25, alignSelf: 'flex-start' }}
            />
          }
          label={
          <Typography variant="body2">{field.label}
        {!!field.helperText && (
          <FormHelperText >{field.helperText}</FormHelperText>
        )}  
          </Typography>}
        />
        
      </Stack>
    );
  }

  renderText(field) {
    const listId = `${this.uid}-${field.field_name}`;
    const hasSuggestions = Array.isArray(field.suggestions) && field.suggestions.length > 0;
    const isTextarea = field.datatype === 'textarea';
    const isNumber = field.datatype === 'number';

    return (
      <div key={field.field_name}>
        <TextField
          fullWidth
          multiline={isTextarea}
          minRows={isTextarea ? 3 : undefined}
          type={isNumber ? 'number' : 'text'}
          label={field.label}
          placeholder={field.placeholder}
          helperText={field.helperText}
          value={this.getTextValue(field)}
          onChange={(e) => this.onTextChange(field, e.target.value)}
          inputProps={!isTextarea && hasSuggestions ? { list: listId } : undefined}
          variant="outlined"
          size="small"
        />
        {!isTextarea && hasSuggestions && (
          <datalist id={listId}>
            {field.suggestions.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        )}
      </div>
    );
  }

  renderJsonEditor(label, helperText) {
    return (
      <TextField
        fullWidth
        multiline
        minRows={4}
        label={label}
        helperText={this.state.jsonError || helperText}
        error={!!this.state.jsonError}
        placeholder="{}"
        value={this.state.jsonDraft}
        onChange={this.onJsonChange}
        variant="outlined"
        size="small"
      />
    );
  }

  render() {
    const fields = this.fieldList;
    if (!fields.length) return null;

    const known = fields.map((f) => f.field_name);
    const extraKeys = Object.keys(this.attribs).filter((k) => !known.includes(k));

    return (
      <Stack spacing={1.25} className="attribs-fields">
        {fields.map((field) =>
          field.datatype === 'boolean' ? this.renderBoolean(field) : this.renderText(field)
        )}

        <Button
          size="small"
          color="secondary"
          onClick={() => this.setState((s) => ({ rawOpen: !s.rawOpen }))}
          startIcon={<i className={`fas fa-caret-${this.state.rawOpen ? 'down' : 'right'}`} />}
          sx={{ alignSelf: 'flex-start', px: 0, minWidth: 0 }}
        >
          Advanced (raw JSON)
          {extraKeys.length > 0 &&
            ` — ${extraKeys.length} other key${extraKeys.length > 1 ? 's' : ''}`}
        </Button>

        <Collapse in={this.state.rawOpen} unmountOnExit>
          {this.renderJsonEditor(
            'Attributes (JSON)',
            'Edits here stay in sync with the fields above. Keys without a field are kept as-is.'
          )}
        </Collapse>
      </Stack>
    );
  }
}
